import AsyncQueue from "./AsyncQueue";
import WebAPIError from "./WebAPIError";
import HTTPError from "./HTTPError";

async function parseResponse (response) {
  return {
    status: response.status,
    payload: response.headers.get("content-type").startsWith("application/json") ? await response.json().catch(() => ({})) : await response.text().catch(() => "")
  };
}

class RequestHandler {
  constructor (manager) {
    this.manager = manager;
    this.queue = new AsyncQueue();
  }

  async push (request) {
    await this.queue.wait();
    try {
      return await this.execute(request);
    } finally {
      this.queue.shift();
    }
  }

  get _inactive () {
    return this.queue.remaining === 0;
  }

  async execute (request) {
    // Perform the request
    let res;
    try {
      res = await request.make();
    } catch (error) {
      // Retry the specified number of times for request abortions
      if (request.retries === this.manager.options.retryLimit) {
        throw new HTTPError(error.message, error.constructor.name, error.status, request.method, request.path);
      }

      request.retries++;
      return this.execute(request);
    }
    
    // Handle 2xx and 3xx responses
    if (res.ok) {
      // Nothing wrong with the request, proceed with the next one
      return parseResponse(res);
    }

    // Handle 4xx responses
    if (res.status >= 400 && res.status < 500) {
      if (res.status === 429) return this.execute(request);

      // Handle possible malformed requests
      let data;
      try {
        data = await parseResponse(res);
      } catch (err) {
        throw new HTTPError(err.message, err.constructor.name, err.status, request.method, request.path);
      }

      throw new WebAPIError(request.path, data, request.method, res.status);
    }

    // Handle 5xx responses
    if (res.status >= 500 && res.status < 600) {
      // Retry the specified number of times for possible serverside issues
      if (request.retries === this.manager.options.retryLimit) {
        throw new HTTPError(res.statusText, res.constructor.name, res.status, request.method, request.path);
      }

      request.retries++;
      return this.execute(request);
    }

    return null;
  }
}

export default RequestHandler;
